import { createAction, createActionGroup, emptyProps, props } from '@ngrx/store';

import { DiagStackNode, DiagStackNodeError, RemoteDiagnosticsSession } from '@vi-iot/client';

import {
  ActiveErrorWithDiagStackDetail,
  CurrentProductMatrix,
  DIAGNOSTICS_CONNECTION_STATUS,
  DIAGNOSTICS_CONNECTION_STEP,
  DiagStackBulkScheduleReadSchedulerOutcomeResolved,
  RemoteDiagnosticsActiveSession,
  RemoteDiagnosticsControlUnit,
  RemoteDiagnosticsParameter,
  RemoteDiagnosticsVddListParameter,
  SetupInfo,
} from './remote-diagnostics.model';

export namespace RemoteDiagnosticsActions {
  export const setActiveConnectionStep = createAction(
    '[REMOTE DIAGNOSTICS] Set Active Connection Step',
    props<{ payload: DIAGNOSTICS_CONNECTION_STEP }>()
  );

  export const setConnectionStatus = createAction(
    '[REMOTE DIAGNOSTICS] Set Connection Status',
    props<{ payload: DIAGNOSTICS_CONNECTION_STATUS }>()
  );

  export const saveConnectionReason = createAction(
    '[REMOTE DIAGNOSTICS] Save connection reason',
    props<{ connectionReason: string }>()
  );

  export const login = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Login',
    events: {
      attempt: props<{ gatewaySerial: string }>(),
      success: props<{
        gatewaySerial: string;
        remoteDiagnosticsSession: RemoteDiagnosticsSession;
        loginId: string;
      }>(),
      failed: props<{ gatewaySerial: string }>(),
    },
  });

  export const openSession = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Open session',
    events: {
      attempt: props<{ gatewaySerial: string }>(),
      success: props<{
        gatewaySerial: string;
        guid: string;
      }>(),
      failed: props<{ gatewaySerial: string }>(),
    },
  });

  export const closeSession = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Close session',
    events: {
      attempt: props<{ gatewaySerial: string }>(),
      success: props<{
        gatewaySerial: string;
      }>(),
      failed: props<{ gatewaySerial: string }>(),
      aborted: props<{ gatewaySerial: string }>(),
    },
  });

  export const setupInfo = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Setup info',
    events: {
      attempt: props<{ gatewaySerial: string }>(),
      success: props<{
        gatewaySerial: string;
        setupInfo: SetupInfo;
      }>(),

      failed: emptyProps(),
    },
  });

  export const remoteVciRegister = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Remote vci register',
    events: {
      attempt: props<{ gatewaySerial: string; technicianWsUrl: string }>(),
      success: props<{ gatewaySerial: string; technicianWsUrl: string }>(),
      failed: props<{ gatewaySerial: string }>(),
    },
  });

  export const remoteVciAvailabilityCheck = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Remote vci availability check',
    events: {
      attempt: props<{ gatewaySerial: string }>(),
      'response empty': emptyProps(),
      success: props<{
        gatewaySerial: string;
        remoteVciName: string;
        resource: string;
        vinNumber: string;
      }>(),
      failed: props<{ gatewaySerial: string }>(),
    },
  });

  export const vciConnect = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Remote VCI connect ',
    events: {
      attempt: props<{ gatewaySerial: string }>(),
      success: emptyProps(),
      failed: props<{ gatewaySerial: string }>(),
    },
  });

  export const mainLogicalLinkOpen = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Main logical link open',
    events: {
      attempt: props<{ gatewaySerial: string }>(),
      success: props<{ gatewaySerial: string; logicalLinkName: string }>(),
      failed: props<{ gatewaySerial: string }>(),
    },
  });

  export const fetchProductMatrix = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Fetch product matrix',
    events: {
      attempt: props<{ gatewaySerial: string }>(),
      success: props<{ gatewaySerial: string; filteredProductMatrix: CurrentProductMatrix[] }>(),
      failed: props<{ gatewaySerial: string }>(),
      incorrectData: props<{ gatewaySerial: string }>(),
    },
  });

  export const openDeviceMainLogicalLink = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Device main logical link open',
    events: {
      attempt: props<{ gatewaySerial: string; mainLogicalLinkFromProductMatrix: string }>(),
      success: props<{ mainLogicalLinkFromProductMatrix: string }>(),
      failed: emptyProps(),
    },
  });

  export const fetchDeviceTopology = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Fetch device topology',
    events: {
      attempt: props<{ gatewaySerial: string; mainLogicalLinkFromProductMatrix: string }>(),
      success: emptyProps(),
      failed: props<{ gatewaySerial: string }>(),
    },
  });

  export const mainLogicalLinkClose = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Main logical link close',
    events: {
      attempt: props<{ gatewaySerial: string }>(),
      success: props<{ gatewaySerial: string; logicalLinkName: string }>(),
      failed: props<{ gatewaySerial: string }>(),
    },
  });

  export const mainLogicalLinkFromTopologyOpen = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Main logical link from topology open',
    events: {
      attempt: props<{ gatewaySerial: string }>(),
      success: props<{ gatewaySerial: string; logicalLinkName: string }>(),
      failed: props<{ gatewaySerial: string }>(),
      skipped: props<{ gatewaySerial: string }>(),
    },
  });

  export const saveBusDetailsFromTopology = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Save bus details from topology',
    events: {
      success: props<{
        gatewaySerial: string;
        busDetailsFromTopology: ActiveErrorWithDiagStackDetail[];
      }>(),
      failed: emptyProps(),
    },
  });

  export const updateBusDetailsFromDtc = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Save bus details from active DTC',
    events: {
      success: props<{
        gatewaySerial: string;
        busDetailsFromTopology: ActiveErrorWithDiagStackDetail[];
      }>(),
      failed: props<{
        gatewaySerial: string;
      }>(),
    },
  });

  export const saveMainLogicalLinkFromTopology = createAction(
    '[REMOTE DIAGNOSTICS] Save main logical link from topology',

    props<{
      gatewaySerial: string;
      logicalLinkName: string;
    }>()
  );

  export const saveBundleIdFromTopology = createAction(
    '[REMOTE DIAGNOSTICS] Save bundleId from topology',

    props<{
      gatewaySerial: string;
      bundleId: string;
    }>()
  );

  export const logicalLinkOpen = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Logical link open',
    events: {
      attempt: props<{
        gatewaySerial: string;
        logicalLinkName: string;
        loadVddList: boolean;
      }>(),
      abort: props<{ gatewaySerial: string; logicalLinkName: string }>(),
      success: props<{ gatewaySerial: string; logicalLinkName: string }>(),
      failed: props<{ gatewaySerial: string; error: any }>(),
    },
  });

  export const openLogicalLinkBeforeVddReading = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Try open Logical link before vdd read',
    events: {
      attempt: props<{
        gatewaySerial: string;
        logicalLinkName: string;
        vddNames: string[];
      }>(),
      success: props<{ gatewaySerial: string; logicalLinkName: string; vddNames: string[] }>(),
      'already open': props<{
        gatewaySerial: string;
        logicalLinkName: string;
        vddNames: string[];
      }>(),
      failed: props<{ gatewaySerial: string }>(),
    },
  });

  export const disconnect = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Disconnect',
    events: {
      attempt: props<{ gatewaySerial: string }>(),
      success: props<{ gatewaySerial: string }>(),
      failed: props<{ gatewaySerial: string; error: any }>(),
    },
  });

  export const loadActiveSessions = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Load active sessions from backend storage attempt',
    events: {
      attempt: emptyProps(),
      success: props<{ activeSessions: RemoteDiagnosticsActiveSession[] }>(),
      failed: emptyProps(),
    },
  });

  export const updateActiveSessions = createAction(
    '[REMOTE DIAGNOSTICS] Update active sessions',
    props<{ activeSessions: RemoteDiagnosticsActiveSession[] }>()
  );

  export const loadControlUnits = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Load control units',
    events: {
      attempt: props<{ gatewaySerial: string; logicalLinkName: string }>(),
      success: props<{
        gatewaySerial: string;
        controlUnits: RemoteDiagnosticsControlUnit[];
      }>(),
      failed: props<{ gatewaySerial: string }>(),
    },
  });

  export const loadVddList = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Load vdd list attempt',
    events: {
      attempt: props<{ gatewaySerial: string; logicalLinkName: string }>(),
      success: props<{
        gatewaySerial: string;
        parameters: RemoteDiagnosticsVddListParameter[];
      }>(),
      failed: props<{ error: any }>(),
    },
  });

  export const selectControlUnit = createAction(
    '[REMOTE DIAGNOSTICS] Select control unit',
    props<{ controlUnit: RemoteDiagnosticsControlUnit; gatewaySerial: string }>()
  );

  export const parameter = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Parameter',
    events: {
      add: props<{
        controlUnit: RemoteDiagnosticsControlUnit;
        vdd: RemoteDiagnosticsVddListParameter;
        gatewaySerial: string;
      }>(),
      delete: props<{ parameterId: number; gatewaySerial: string }>(),
      change: props<{ parameter: RemoteDiagnosticsParameter; gatewaySerial: string }>(),
    },
  });

  export const allParameters = createActionGroup({
    source: 'REMOTE DIAGNOSTICS All parameters',
    events: {
      select: props<{ gatewaySerial: string }>(),
      unselect: props<{ gatewaySerial: string }>(),
      'change interval': props<{ interval: number; gatewaySerial: string }>(),
    },
  });

  export const deleteSelectedParameters = createAction(
    '[REMOTE DIAGNOSTICS] Delete selected parameters',
    props<{ gatewaySerial: string }>()
  );

  export const deleteUseCaseParameters = createAction(
    '[REMOTE DIAGNOSTICS] Delete use case parameters',
    props<{ gatewaySerial: string }>()
  );

  export const scheduleMultipleParametersReadCycle = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Schedule multiple parameters read cycle',
    events: {
      attempt: props<{ gatewaySerial: string; interval: number }>(),
      outcome: props<{
        gatewaySerial: string;
        transactionOutcome: DiagStackBulkScheduleReadSchedulerOutcomeResolved;
      }>(),
      failed: emptyProps(),
    },
  });

  export const scheduleParameterReadCycle = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Schedule parameter read cycle',
    events: {
      attempt: props<{ parameter: RemoteDiagnosticsParameter; gatewaySerial: string }>(),
      success: props<{
        parameterId: number;
        requestId: string;
        gatewaySerial: string;
      }>(),
      failed: emptyProps(),
    },
  });

  export const cancelParameterReadCycle = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Cancel parameter read cycle',
    events: {
      attempt: props<{ parameter: RemoteDiagnosticsParameter; gatewaySerial: string }>(),
      success: props<{ parameterId: number; gatewaySerial: string }>(),
      failed: emptyProps(),
    },
  });

  export const cancelMultipleParametersReadCycle = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Cancel multiple parameters read cycle',
    events: {
      attempt: props<{ gatewaySerial: string; onlySelected: boolean }>(),
      outcome: props<{
        gatewaySerial: string;
        transactionOutcome: DiagStackBulkScheduleReadSchedulerOutcomeResolved;
      }>(),
      failed: emptyProps(),
      aborted: props<{ gatewaySerial: string }>(), // nothing to cancel
    },
  });

  export const logicalLinkList = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Logical link list get',
    events: {
      attempt: props<{ gatewaySerial: string }>(),
      success: props<{ logicalLinkList: string[] }>(),
      failed: props<{ error: any }>(),
    },
  });

  export const lastUserActivityTimestampReset = createAction(
    '[REMOTE DIAGNOSTICS] Last user activity timestamp reset',
    props<{ gatewaySerial: string }>()
  );

  export const diagstackGetFreeInstance = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Get free diagstack instance',
    events: {
      attempt: props<{ gatewaySerial: string; deviceSerial: string }>(),
      success: props<{
        gatewaySerial: string;
        deviceSerial: string;
        diagStackNode: DiagStackNode;
      }>(),
      failed: props<{ gatewaySerial: string; error: DiagStackNodeError }>(),
    },
  });

  export const refreshActiveSessions = createActionGroup({
    source: 'REMOTE DIAGNOSTICS Refresh active sessions',
    events: {
      success: props<{ gatewaySerial: string; sessions: RemoteDiagnosticsActiveSession[] }>(),
      empty: props<{ gatewaySerial: string; sessions: RemoteDiagnosticsActiveSession[] }>(),
    },
  });

  export const tabChanged = createAction('[REMOTE DIAGNOSTICS] Tab changed');

  export const getConfirmPopupFlag = createActionGroup({
    source: '[REMOTE DIAGNOSTICS] Get popup state',
    events: {
      attempt: emptyProps(),
      success: props<{ isUseCasePopupBlocked: boolean }>(),
      error: emptyProps(),
    },
  });
  export const saveConfirmPopupFlag = createActionGroup({
    source: '[REMOTE DIAGNOSTICS] Save popup state',
    events: {
      attempt: emptyProps(),
      success: emptyProps(),
      error: emptyProps(),
    },
  });

  export const stopAndClearParamsOnTabChange = createAction(
    '[REMOTE DIAGNOSTICS] Stop and clear parameters on tab change'
  );

  export const manageChartCollapsibility = createActionGroup({
    source: '[REMOTE DIAGNOSTICS] Manage chart collapsibility',
    events: {
      show: emptyProps(),
      hide: emptyProps(),
      toggle: emptyProps(),
    },
  });
}
